import * as React from "react";
import { 
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import { useToast } from "../../../components/shared/ToastProvider";

/**
 * Add Condo Unit Modal Component
 * Reusable modal for creating new condo units
 * UI only - Backend developer will add form handling and API calls
 */
export default function CondoUnitFormModal({
  open,
  onClose,
  title = "Add Condo Unit",
  primaryActionLabel = "Create"
}) {
  const { showToast } = useToast();
  // Hardcoded rooms data for UI display
  const rooms = [
    { name: "", capacity: 0 }
  ];

  const handlePrimaryClick = () => {
    // UI-only: backend will handle actual save later.
    showToast({ message: "Condo unit created", severity: "success" });
    if (onClose) {
      onClose();
    }
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handlePrimaryClick}>
        {primaryActionLabel}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      actions={actions}
      maxWidth="sm"
    >
      <div className="modal-form-grid" style={{ marginTop: 8 }}>
        {/* Condo - Required */}
        <div className="modal-form-row">
          <FormControl fullWidth required size="small">
            <InputLabel>Condo</InputLabel>
            <Select
              defaultValue=""
              label="Condo"
            >
              <MenuItem value="">-</MenuItem>
              <MenuItem value="oriental">Oriental KH</MenuItem>
              <MenuItem value="141">141 Apartment</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Unit & Floor Area - One Line */}
        <div className="modal-form-row modal-form-row--two-cols">
          <TextField
            fullWidth
            required
            size="small"
            label="Unit"
          />
          <TextField
            fullWidth
            size="small"
            label="Floor Area"
          />
        </div>

        {/* Parking Slot & Parking Slot Details - One Line */}
        <div className="modal-form-row modal-form-row--two-cols">
          <TextField
            fullWidth
            size="small"
            label="Parking Slot"
          />
          <TextField
            fullWidth
            size="small"
            label="Parking Slot Details"
          />
        </div>

        {/* Furnish Type - Required */}
        <FormControl fullWidth required size="small">
          <InputLabel>FurnishType</InputLabel>
          <Select
            defaultValue=""
            label="FurnishType"
          >
            <MenuItem value="">-</MenuItem>
            <MenuItem value="fully">Fully Furnished</MenuItem>
            <MenuItem value="semi">Semi Furnished</MenuItem>
            <MenuItem value="unfurnished">Unfurnished</MenuItem>
          </Select>
        </FormControl>

        {/* Unit Phone, Unit Leader & In-Charge - One Line */}
        <div className="modal-form-row modal-form-row--three-cols">
          <TextField
            fullWidth
            size="small"
            label="Unit Phone"
          />
          <FormControl fullWidth size="small">
            <InputLabel>Unit Leader</InputLabel>
            <Select
              defaultValue=""
              label="Unit Leader"
            >
              <MenuItem value="">-</MenuItem>
              <MenuItem value="leader1">Leader 1</MenuItem>
              <MenuItem value="leader2">Leader 2</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>In-Charge</InputLabel>
            <Select
              defaultValue=""
              label="In-Charge"
            >
              <MenuItem value="">-</MenuItem>
              <MenuItem value="person1">Person 1</MenuItem>
              <MenuItem value="person2">Person 2</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Maid Name & Maid Number - One Line */}
        <div className="modal-form-row modal-form-row--two-cols">
          <TextField
            fullWidth
            size="small"
            label="Maid Name"
          />
          <TextField
            fullWidth
            size="small"
            label="Maid Number"
          />
        </div>

        {/* Allowance - Required */}
        <div className="modal-form-row modal-form-row--two-cols">
          <FormControl fullWidth required size="small">
            <InputLabel>Allowance Currency</InputLabel>
            <Select
              defaultValue="PHP"
              label="Allowance Currency"
            >
              <MenuItem value="PHP">PHP</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="KHR">KHR</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Allowance Amount"
            placeholder="0.00"
            inputProps={{ step: "0.01" }}
          />
        </div>

        {/* Rooms Table */}
        <div>
          <label className="modal-field-label">Rooms</label>
          <TableContainer component={Paper} className="modal-table-container">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className="modal-table-header">Name</TableCell>
                  <TableCell className="modal-table-header">Capacity</TableCell>
                  <TableCell className="modal-table-header modal-table-actions-cell">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rooms.map((room, index) => (
                  <TableRow key={index}>
                    <TableCell className="modal-table-cell">
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Room name"
                      />
                    </TableCell>
                    <TableCell className="modal-table-cell">
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        defaultValue={0}
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell className="modal-table-cell">
                      <Button
                        size="small"
                        variant="text"
                        className="modal-action-button"
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        className="modal-action-button"
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            fullWidth
          >
            + Add Room(s)
          </Button>
        </div>

        {/* Condo Company & Condo Sharing - One Line */}
        <div className="modal-form-row modal-form-row--two-cols">
          <FormControl fullWidth required size="small">
            <InputLabel>Condo Company</InputLabel>
            <Select
              defaultValue=""
              label="Condo Company"
            >
              <MenuItem value="">-</MenuItem>
              <MenuItem value="company1">Company 1</MenuItem>
              <MenuItem value="company2">Company 2</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth required size="small">
            <InputLabel>Condo Sharing</InputLabel>
            <Select
              defaultValue=""
              label="Condo Sharing"
            >
              <MenuItem value="">-</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Remarks - Moved to bottom */}
        <TextField
          fullWidth
          size="small"
          multiline
          rows={2}
          label="Remarks"
        />
      </div>
    </Modal>
  );
}
